/**
 * Pure renderer for a banner config at any pixel size — used by the
 * editor canvas, preset thumbnails and anything else that needs to show
 * a banner. Mirrors helper/banner_backend.py so preview == final video.
 */
import React, { useEffect, useRef, useState } from 'react';
import { resolveMediaUrl } from './uploadHelper';
import { hasImageShape, imageShapeClipPath, imageShapeSvgPoints } from './imageShapes';
import {
  TEXT_DEFAULTS,
  fontCss,
  isCircleImageLayer,
  layerBox,
} from './bannerPresets';

export function hexToRgba(hex, alpha = 1) {
  if (!hex || typeof hex !== 'string') return `rgba(0,0,0,${alpha})`;
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full.slice(0, 6), 16);
  if (!Number.isFinite(n)) return `rgba(0,0,0,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

export function layerImageSrc(layer) {
  return resolveMediaUrl(layer.imageUrl || layer.image_url || '');
}

const textValue = (layer) => {
  const t = String(layer.text ?? '');
  if (layer.textTransform === 'uppercase') return t.toUpperCase();
  if (layer.textTransform === 'lowercase') return t.toLowerCase();
  return t;
};

/**
 * One layer, absolutely positioned in a canvas of `dims` px.
 * `scale` = dims.h / export height (for px-based values like image borders).
 */
export function LayerView({ layer, dims, scale = 1, showPlaceholders = true }) {
  if (layer.visible === false) return null;
  const box = layerBox(layer, dims);
  const base = {
    position: 'absolute',
    left: box.cx - box.w / 2,
    top: box.cy - box.h / 2,
    width: box.w,
    height: box.h,
    transform: box.rot ? `rotate(${box.rot}deg)` : undefined,
    opacity: layer.opacity ?? 1,
    boxSizing: 'border-box',
    pointerEvents: 'none',
  };
  const H = dims.h;

  if (layer.type === 'fill') {
    return <div style={{ ...base, background: layer.color || '#0f172a' }} />;
  }

  if (layer.type === 'gradient') {
    const bg = layer.gradientType === 'radial'
      ? `radial-gradient(ellipse farthest-corner at 50% 50%, ${layer.gradientFrom}, ${layer.gradientTo})`
      : `linear-gradient(${layer.gradientAngle ?? 135}deg, ${layer.gradientFrom}, ${layer.gradientTo})`;
    return <div style={{ ...base, background: bg }} />;
  }

  if (layer.type === 'shape') {
    const radius = layer.shapeKind === 'ellipse'
      ? '50%'
      : `${Math.min(box.w, box.h) * Math.min(0.5, layer.borderRadiusPct || 0)}px`;
    const strokePx = layer.strokeEnabled ? Math.max(1, (layer.strokeWidthPct || 0) * H) : 0;
    return (
      <div
        style={{
          ...base,
          borderRadius: radius,
          background: layer.fillEnabled === false ? 'transparent' : (layer.color || '#7c3aed'),
          boxShadow: strokePx ? `inset 0 0 0 ${strokePx}px ${layer.strokeColor || '#ffffff'}` : undefined,
        }}
      />
    );
  }

  if (layer.type === 'image' && hasImageShape(layer)) {
    // picture cut to a shape — same outline points as the renderer
    const src = layerImageSrc(layer);
    const borderPx = (layer.borderWidth || 0) * scale;
    const svgPts = imageShapeSvgPoints(layer.imageShape, box.w, box.h);
    const clipId = `imgshape-${String(layer.id || 'x').replace(/[^a-z0-9_-]/gi, '')}`;
    return (
      <div style={base}>
        <div style={{ position: 'absolute', inset: 0, clipPath: imageShapeClipPath(layer.imageShape, box.w, box.h) }}>
          {src ? (
            <img
              src={src}
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: layer.objectFit || 'cover',
                objectPosition: `${layer.imageFocusXPct ?? 50}% ${layer.imageFocusYPct ?? 50}%`,
              }}
            />
          ) : showPlaceholders ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.14) 0 8px, rgba(255,255,255,0.05) 8px 16px)',
                color: 'rgba(255,255,255,0.7)',
                fontSize: Math.max(9, Math.min(14, box.h * 0.12)),
                fontWeight: 600,
              }}
            >
              {box.w > 60 ? 'Image' : ''}
            </div>
          ) : null}
        </div>
        {borderPx > 0 && svgPts && (
          <svg width={box.w} height={box.h} viewBox={`0 0 ${box.w} ${box.h}`} style={{ position: 'absolute', inset: 0, overflow: 'visible' }} aria-hidden="true">
            <defs>
              <clipPath id={clipId}><polygon points={svgPts} /></clipPath>
            </defs>
            <polygon
              points={svgPts}
              fill="none"
              stroke={layer.borderColor || '#ffffff'}
              strokeWidth={borderPx * 2}
              strokeLinejoin="round"
              clipPath={`url(#${clipId})`}
            />
          </svg>
        )}
      </div>
    );
  }

  if (layer.type === 'image') {
    const src = layerImageSrc(layer);
    const radius = isCircleImageLayer(layer)
      ? '50%'
      : layer.borderRadiusPct ? `${Math.min(box.w, box.h) * Math.min(0.5, layer.borderRadiusPct)}px` : 0;
    const borderPx = (layer.borderWidth || 0) * scale;
    return (
      <div style={{ ...base, borderRadius: radius, overflow: 'hidden' }}>
        {src ? (
          <img
            src={src}
            alt=""
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: layer.objectFit || 'cover',
              objectPosition: `${layer.imageFocusXPct ?? 50}% ${layer.imageFocusYPct ?? 50}%`,
            }}
          />
        ) : showPlaceholders ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.06) 0 8px, rgba(255,255,255,0.02) 8px 16px)',
              color: 'rgba(255,255,255,0.55)',
              fontSize: Math.max(9, Math.min(14, box.h * 0.12)),
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            {box.w > 60 ? 'Image' : ''}
          </div>
        ) : null}
        {borderPx > 0 && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: radius,
              boxShadow: `inset 0 0 0 ${borderPx}px ${layer.borderColor || '#ffffff'}`,
            }}
          />
        )}
      </div>
    );
  }

  if (layer.type === 'text') {
    const t = { ...TEXT_DEFAULTS, ...layer };
    const fontPx = Math.max(6, H * (t.fontSizePct ?? 0.065));
    const strokePx = (t.strokeWidthPct || 0) * H;
    const padPx = (t.paddingPct ?? 0.004) * H;
    const shadow = t.shadowEnabled !== false
      ? `${(t.shadowOffsetXPct || 0) * H}px ${(t.shadowOffsetYPct || 0) * H}px ${(t.shadowBlurPct || 0) * H}px ${hexToRgba(t.shadowColor, t.shadowOpacity ?? 0.45)}`
      : undefined;
    const align = t.textAlign || 'center';
    return (
      <div
        style={{
          ...base,
          display: 'flex',
          alignItems: 'center',
          padding: `0 ${padPx}px`,
          borderRadius: t.bgEnabled ? Math.min(box.w, box.h) * Math.min(0.5, t.bgRadiusPct || 0) : undefined,
          background: t.bgEnabled ? hexToRgba(t.bgColor, t.bgOpacity ?? 0.6) : undefined,
          overflow: 'visible',
        }}
      >
        <div
          style={{
            width: '100%',
            color: t.color || '#ffffff',
            fontFamily: fontCss(t.fontFamily),
            fontWeight: t.fontWeight || '700',
            fontSize: fontPx,
            lineHeight: t.lineHeight ?? 1.15,
            letterSpacing: `${(t.letterSpacing ?? 0) * 0.1}em`,
            textAlign: align,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'normal',
            wordBreak: 'normal',
            textShadow: shadow,
            WebkitTextStroke: strokePx > 0 ? `${strokePx * 2}px ${t.strokeColor || '#000000'}` : undefined,
            paintOrder: strokePx > 0 ? 'stroke fill' : undefined,
          }}
        >
          {textValue(t)}
        </div>
      </div>
    );
  }

  return null;
}

/** Whole banner at `dims` px. Layers sorted by zIndex. */
export function BannerScene({ config, dims, exportDims, showPlaceholders = true, innerRef, style }) {
  const layers = Array.isArray(config?.layers) ? config.layers : [];
  const sorted = [...layers].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
  const scale = exportDims ? dims.h / exportDims.h : 1;
  return (
    <div
      ref={innerRef}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        background: '#000',
        transformOrigin: '50% 50%',
        ...style,
      }}
    >
      {sorted.map((layer) => (
        <LayerView key={layer.id} layer={layer} dims={dims} scale={scale} showPlaceholders={showPlaceholders} />
      ))}
    </div>
  );
}

/** Track an element's content size. */
export function useElementSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize((prev) => (Math.abs(prev.w - width) < 0.5 && Math.abs(prev.h - height) < 0.5 ? prev : { w: width, h: height }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

/** Small static thumbnail that fills its (aspect-ratio) container. */
export function BannerThumb({ config, orientation = 'landscape' }) {
  const [ref, size] = useElementSize();
  const portrait = orientation === 'portrait';
  const exportDims = portrait ? { w: 1080, h: 1920 } : { w: 1920, h: 1080 };
  const ar = exportDims.w / exportDims.h;
  let w = size.w;
  let h = w / ar;
  if (size.h && h > size.h) { h = size.h; w = h * ar; }
  return (
    <div ref={ref} style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {w > 0 && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: w,
            height: h,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <BannerScene config={config} dims={{ w, h }} exportDims={exportDims} />
        </div>
      )}
    </div>
  );
}
